import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@design-system';
import { selectSessionAdvisor } from '@client/model/selectors';

interface PartnerLegacyAttestationDialogProps {
  open: boolean;
  isSharing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Batch legacy-client attestation (Modal 2). Shown after the connect dialog when the advisor has
// pre-new-consent clients, and from the Distribution Partner settings panel. Confirming shares all
// of those earlier clients with the connected partner contacts; "Not now" proceeds without them.
export function PartnerLegacyAttestationDialog({
  open,
  isSharing,
  onConfirm,
  onCancel,
}: PartnerLegacyAttestationDialogProps) {
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const orgLegalName = sessionAdvisor?.organizationName || 'your firm';
  const [attested, setAttested] = useState(false);

  useEffect(() => {
    if (open) setAttested(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !isSharing && onCancel()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">
            Do you have consent to share your earlier clients with your distribution partner(s)?
          </DialogTitle>
          <DialogDescription className="text-foreground">
            Clients who joined before July 2, 2026 consented to share their data with you and{' '}
            {orgLegalName}, but not with other partners you collaborate with. To include these
            clients, confirm below that you have their consent to share their data with your
            distribution partner(s).
          </DialogDescription>
        </DialogHeader>

        <label
          htmlFor="partner-legacy-attestation"
          className="bg-muted/50 hover:bg-muted flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
        >
          <Checkbox
            id="partner-legacy-attestation"
            checked={attested}
            onCheckedChange={checked => setAttested(checked === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="partner-legacy-attestation"
            className="text-foreground cursor-pointer text-sm font-normal"
          >
            By checking this box, I represent and warrant that, for each of my existing clients
            whose data I am choosing to share, I have obtained and currently hold the appropriate
            consents required under applicable law to share that client's personal data with third
            party partners such as my distribution partner.
          </Label>
        </label>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSharing}
          >
            Not now
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!attested || isSharing}
          >
            {isSharing ? (
              <>
                <Loader2 className="mr-1 size-4 animate-spin" />
                Sharing…
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
